import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ReactSVG } from 'react-svg';
import { SiloTypes } from './CreateOrEditSiloForm';
import { TFunction } from 'i18next';
import { Button, sortByProperty } from '@web/utils/';
import binIcon from '@web/utils/src/assets/icons/functions/bin-icon.svg';
import feedIcon from '@web/utils/src/assets/icons/functions/feed-icon.svg';
import { StyledTable } from '@/commons/ui/theme/materialStyles';
import { Silo, Sort } from '@/types';
import {
  Box,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
} from '@mui/material';

type PSilo = Pick<
  Silo,
  'id' | 'name' | 'icon' | 'capacity' | 'freeCapacity' | 'createdAt'
>;

interface SilosTableProps {
  silos: PSilo[];
  handleEdit: (silo) => void;
}

type TableKey = keyof PSilo;

const TABLE_HEAD = (
  t: TFunction,
): {
  id: TableKey;
  label: string;
  alignRight: boolean;
}[] => [
  {
    id: 'id',
    label: t('farm.silos.id'),
    alignRight: false,
  },
  {
    id: 'name',
    label: t('farm.silos.name'),
    alignRight: false,
  },
  {
    id: 'capacity',
    label: t('farm.silos.capacity'),
    alignRight: false,
  },
  // {
  //   id: 'freeCapacity',
  //   label: t('farm.silos.last_article'),
  //   alignRight: false,
  // },
];

const visuallyHidden = {
  border: 0,
  margin: -1,
  padding: 0,
  width: '1px',
  height: '1px',
  overflow: 'hidden',
  position: 'absolute',
  whiteSpace: 'nowrap',
  clip: 'rect(0 0 0 0)',
};

const SilosTable: React.FC<SilosTableProps> = (props) => {
  const { t } = useTranslation();
  const { silos, handleEdit } = props;
  const [orderBy, setOrderBy] = useState<TableKey>('id');
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');

  const handleRequestSort = (_e, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const createSortHandler = (property) => (event) => {
    handleRequestSort(event, property);
  };

  return (
    <div className="py-2-0">
      <StyledTable>
        <TableHead>
          <TableRow>
            {TABLE_HEAD(t).map((headCell) => (
              <TableCell
                key={headCell.id}
                align={headCell.alignRight ? 'right' : 'left'}
                sortDirection={orderBy === headCell.id ? order : false}
                style={{ backgroundColor: 'white' }}
              >
                <TableSortLabel
                  hideSortIcon
                  active={orderBy === headCell.id}
                  direction={orderBy === headCell.id ? order : 'asc'}
                  onClick={createSortHandler(headCell.id)}
                >
                  <span className="text-body1 font-rg capitalize">
                    {headCell.label}
                  </span>
                  {orderBy === headCell.id ? (
                    <Box sx={{ ...visuallyHidden }}>
                      {order === 'desc'
                        ? 'sorted descending'
                        : 'sorted ascending'}
                    </Box>
                  ) : null}
                </TableSortLabel>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {sortByProperty(silos, 'createdAt', Sort.Desc).map((silo) => (
            <TableRow
              hover
              key={silo.id}
              tabIndex={-1}
              role="checkbox"
              className="relative"
            >
              <TableCell align="left">
                <ReactSVG
                  src={silo.icon === SiloTypes.FLAT ? binIcon : feedIcon}
                  className="w-4-0 h-4-0"
                  beforeInjection={(svg) => {
                    svg.setAttribute('style', 'height: 3.2rem; width:auto');
                  }}
                />
              </TableCell>
              <TableCell align="left">
                <span className="text-1-4 font-md">{silo.name}</span>
              </TableCell>
              <TableCell align="left">
                <span className=" text-1-4 font-md">
                  {silo.freeCapacity} / {silo.capacity}
                </span>
                <Button
                  className="absolute right-0 top-50-percent scale-75 transform -translate-y-50-percent"
                  onClick={() => handleEdit(silo)}
                >
                  {t('farm.silos.edit')}
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </StyledTable>
      {silos.length === 0 && (
        <p className="text-body1 py-4-0 font-bd text-center">
          {t('farm.silos.silos_not_found')}
        </p>
      )}
    </div>
  );
};
export default SilosTable;
